/**
 * withCaptchaPromise Utility
 *
 * Wraps the callback-based withCaptcha function to return a Promise.
 * This makes it easier to use with async/await instead of callbacks.
 *
 * Usage:
 *   const result = await withCaptchaPromise('checkout', async (rcToken) => {
 *     return await someApiCall(rcToken);
 *   }, withCaptcha);
 */

type WithCaptchaFn = (action: string, callback: (rcToken: string) => void) => void;

/**
 * Execute an async function with reCAPTCHA token
 *
 * @param action - reCAPTCHA action name (e.g., 'checkout')
 * @param asyncFn - Function that receives the reCAPTCHA token and returns a Promise
 * @param withCaptcha - App-specific callback that loads reCAPTCHA and invokes callback with token
 * @returns Promise that resolves with the async function's result
 */
export const withCaptchaPromise = <T>(
  action: string,
  asyncFn: (rcToken: string) => Promise<T>,
  withCaptcha: WithCaptchaFn
): Promise<T> => new Promise<T>((resolve, reject) => {
  withCaptcha(action, async (rcToken: string) => {
    try {
      const result = await asyncFn(rcToken);
      resolve(result);
    } catch (error) {
      reject(error);
    }
  });
});
